import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<ButtonVariant, string> = {
  // Uses the CTA tokens, which the tenant theme may neutralise. See styles/index.css.
  primary: 'bg-cta text-cta-fg hover:bg-cta-hover disabled:opacity-50 shadow-xs',
  secondary:
    'bg-surface text-fg border border-border-strong hover:bg-surface-hover disabled:text-fg-disabled shadow-xs',
  ghost: 'text-fg-muted hover:bg-surface-sunken hover:text-fg disabled:text-fg-disabled',
  danger: 'bg-danger text-white hover:bg-danger/90 disabled:bg-danger/50 shadow-xs',
  link: 'text-accent underline-offset-4 hover:underline p-0 h-auto',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1.5',
  md: 'h-8 px-3 text-sm gap-2',
  lg: 'h-10 px-4 text-base gap-2',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /** Render as the child element (e.g. a router Link) while keeping button styles. */
  asChild?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    asChild = false,
    leadingIcon,
    trailingIcon,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  const classes = cn(
    'inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap',
    'transition-colors disabled:cursor-not-allowed',
    variant !== 'link' && SIZES[size],
    VARIANTS[variant],
    className,
  )

  // Radix Slot requires EXACTLY ONE child: it merges props onto that element.
  // Wrapping icons around `children` here would hand it three, which throws
  // "Slot failed to slot onto its children". So an asChild button renders the
  // child alone and lets the caller compose any icon inside it.
  if (asChild) {
    return (
      <Slot ref={ref} className={classes} aria-busy={loading || undefined} {...props}>
        {children}
      </Slot>
    )
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      // Announce the busy state rather than only showing a spinner.
      aria-busy={loading || undefined}
      className={classes}
      {...props}
    >
      {loading ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  )
})

export interface IconButtonProps extends Omit<ButtonProps, 'leadingIcon' | 'trailingIcon'> {
  /** Required: an icon-only control is unusable without an accessible name. */
  label: string
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 'md', className, children, ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      size={size}
      aria-label={label}
      title={label}
      className={cn(
        'px-0',
        size === 'sm' ? 'w-7' : size === 'lg' ? 'w-10' : 'w-8',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  )
})
